/**
 * Настройки сообщества, которые хранятся в базе и правятся из админки.
 *
 * В `.env` остаётся только то, без чего процесс не стартует и что является
 * секретом: токен бота, идентификатор приложения, секрет OAuth, путь к базе.
 * Всё остальное — здесь, чтобы менять без перезапуска и без SSH.
 */

import { tierLabel, type EventType, type IsoTimestamp, type MemberList } from './domain.js';
import { DEFAULT_MESSAGES, type MessageSettings } from './messages.js';
import { DEFAULT_NOTIFICATIONS, type NotificationSettings } from './notifications.js';

/** Кого зовёт анонс набора в канале плюсов. */
export type EventMention = 'everyone' | 'here' | 'none';

export const EVENT_MENTIONS: readonly EventMention[] = ['everyone', 'here', 'none'];
export const MEMBER_LISTS: readonly MemberList[] = ['plus', 'reserve'];

export interface CommunitySettings {
  /**
   * Часовой пояс сообщества (IANA, например `Europe/Moscow`).
   * В нём модератор вводит время набора и в нём же время показывается всем.
   */
  timezone: string;

  /** Типы мероприятий, доступные при создании набора. */
  eventTypes: EventType[];

  /** Вместимость основного состава по умолчанию. */
  defaultCapacity: number;

  /** Сколько строк участников максимум показывать в сообщении Discord. */
  maxVisibleRows: number;

  /** Закрывать ли набор автоматически в момент начала мероприятия. */
  autoCloseOnStart: boolean;

  /**
   * Куда кнопка «КИНУТЬ ПЛЮС» записывает человека.
   *
   * Исторически всегда в замену: состав собирал ростер-менеджер вручную.
   * Сообществу, где записываются сколько влезет, удобнее сразу основа —
   * тогда переполнение уходит в замену само.
   */
  eventJoinTarget: MemberList;

  /** Кого зовёт анонс набора. */
  eventAnnounceMention: EventMention;

  /**
   * Объявлять ли отдельным сообщением уход из основного состава и из замены.
   *
   * Про основу объявлять стоит: место освободилось, и это надо заметить
   * сразу. Про замену — обычно нет, там место ничего не стоит, и канал
   * зарастает сообщениями.
   */
  eventAnnounceLeftMain: boolean;
  eventAnnounceLeftReserve: boolean;

  /**
   * Открывать ли заново закрытый набор, если его перенесли на будущее.
   *
   * Иначе перенесённое мероприятие остаётся без возможности записаться.
   */
  eventReopenOnTimeChange: boolean;

  /** За сколько минут до начала отправлять напоминание. */
  reminderMinutesBefore: number;

  /**
   * Проверять ли перед началом, кто из основного состава в голосовом канале.
   *
   * Отсутствующих бот зовёт упоминанием, а сидящих в чужих каналах
   * переносит в сборный. Перенос — действие резкое, и если сообществу оно
   * не подойдёт, проверка выключается целиком этим переключателем.
   */
  voiceCheckEnabled: boolean;

  /** За сколько минут до начала проверять сбор. */
  voiceCheckMinutesBefore: number;

  /**
   * Через сколько дней после отказа игрок может подать заявку в семью заново.
   * `0` — без ограничения.
   */
  familyReapplyDays: number;

  /**
   * Допустимый возраст в заявке в семью.
   *
   * Прежде было зашито «от 1 до 99» — то есть проверки по сути не было.
   * Сообщества с возрастным порогом отсеивали малолетних вручную.
   */
  familyMinAge: number;
  familyMaxAge: number;

  /** Верх шкалы адекватности в заявке. Низ всегда единица. */
  familyAdequacyMax: number;

  /**
   * Требовать ли ссылку в поле «Откаты».
   *
   * Откаты без ссылки рассматривать нечего, но сообщество, где записи
   * присылают иначе, этой проверкой только мучается.
   */
  familyRequireClipLink: boolean;

  /** Писать ли новичку приветствие в канал «добро пожаловать». */
  welcomeMessageEnabled: boolean;

  /** Выдавать ли роль новичка при входе на сервер. */
  newcomerRoleEnabled: boolean;

  /** Максимум попыток разослать запросы на откаты, прежде чем сдаться. */
  rollbackMaxAttempts: number;

  /**
   * Организация сообщества в игровой службе: номер семьи и её название.
   *
   * Бот определяет их сам по первому же сверенному капту — по тому, в чьём
   * составе больше наших статиков, — и дальше берёт готовое, не гадая
   * заново. Номер можно поправить руками: за чужую семью иногда играет
   * столько наших, что счёт вводит в заблуждение.
   *
   * `null` — ещё не определена.
   */
  combatFamilyId: number | null;
  combatFamilyName: string | null;

  /**
   * Личные сообщения бота: текст и выключатель каждого.
   *
   * Включение напоминания живёт здесь же, а не отдельной настройкой: одно
   * и то же не должно настраиваться в двух местах — забудешь про второе, и
   * выключенное уведомление продолжит уходить.
   */
  notifications: NotificationSettings;

  /**
   * Тексты, которые бот пишет в каналах: панели, приветствия, объявления.
   *
   * Пустой текст означает «взять исходный», поэтому новый текст появляется
   * в коде без миграции — см. `messages.ts`.
   */
  messages: MessageSettings;
}

/**
 * Привязка бота к каналам и ролям сервера.
 *
 * Изначально идентификаторы попадают в `.env` при настройке сервера, но
 * дальше их можно менять в админке: пустое значение означает «взять из
 * окружения», заданное — перекрывает его.
 */
export interface WiringSettings {
  channels: {
    profile: string | null;
    event: string | null;
    eventMcl: string | null;
    mod: string | null;
    welcome: string | null;
    logs: string | null;
    familyApply: string | null;
    rollbackCategory: string | null;
    captureStats: string | null;
  };
  roles: {
    moderator: string | null;
    owner: string | null;
    morethenmod: string | null;
    newcomer: string | null;
    media: string | null;
    starterpack: string | null;
    tier1: string | null;
    tier2: string | null;
    tier3: string | null;
    tier4: string | null;
    aptx: string | null;
    /** Коллер: тот, кто ведёт состав на капте голосом. */
    caller: string | null;
  };
}

export const EMPTY_WIRING: WiringSettings = {
  channels: {
    profile: null,
    event: null,
    eventMcl: null,
    mod: null,
    welcome: null,
    logs: null,
    familyApply: null,
    rollbackCategory: null,
    captureStats: null,
  },
  roles: {
    moderator: null,
    owner: null,
    morethenmod: null,
    newcomer: null,
    media: null,
    starterpack: null,
    tier1: null,
    tier2: null,
    tier3: null,
    tier4: null,
    aptx: null,
    caller: null,
  },
};

/** Понятные названия привязок для интерфейса. */
export const CHANNEL_LABELS: Readonly<Record<keyof WiringSettings['channels'], string>> = {
  profile: 'Регистрация профилей',
  event: 'Плюсы и наборы',
  eventMcl: 'Плюсы MCL и VZZ',
  mod: 'Модераторский канал',
  welcome: 'Добро пожаловать',
  logs: 'Логи сервера',
  familyApply: 'Подача заявки в семью',
  rollbackCategory: 'Категория каналов откатов',
  captureStats: 'Статистика каптов',
};

export const ROLE_LABELS: Readonly<Record<keyof WiringSettings['roles'], string>> = {
  moderator: 'Модератор (mentalcore)',
  owner: 'Владелец (owner)',
  morethenmod: 'Администратор (morethenmod)',
  newcomer: 'Новичок',
  media: 'Медиа',
  starterpack: 'Starterpack',
  // Подписи тиров — из общего словаря: поле привязки в настройках должно
  // называться так же, как тир на всех остальных экранах.
  tier1: tierLabel(1),
  tier2: tierLabel(2),
  tier3: tierLabel(3),
  tier4: tierLabel(4),
  aptx: 'aptx (RP-мероприятия)',
  caller: 'caller (ведёт состав на капте)',
};

/** Значения по умолчанию — применяются при первом запуске и при сбросе. */
export const DEFAULT_SETTINGS: CommunitySettings = {
  timezone: 'Europe/Moscow',
  eventTypes: ['MCL', 'CAPTURE', 'VZZ'],
  defaultCapacity: 10,
  maxVisibleRows: 80,
  autoCloseOnStart: true,
  messages: DEFAULT_MESSAGES,
  // Умолчания повторяют прежнее поведение бота слово в слово: смысл
  // изменения — дать возможность его поменять, а не поменять за сообщество.
  eventJoinTarget: 'reserve',
  eventAnnounceMention: 'everyone',
  eventAnnounceLeftMain: true,
  eventAnnounceLeftReserve: false,
  eventReopenOnTimeChange: true,
  notifications: DEFAULT_NOTIFICATIONS,
  reminderMinutesBefore: 30,
  voiceCheckEnabled: true,
  voiceCheckMinutesBefore: 2,
  familyReapplyDays: 7,
  // Прежние границы проверки возраста: их отсутствие и означало «от 1 до 99».
  familyMinAge: 1,
  familyMaxAge: 99,
  familyAdequacyMax: 10,
  familyRequireClipLink: true,
  welcomeMessageEnabled: true,
  newcomerRoleEnabled: true,
  rollbackMaxAttempts: 5,
  combatFamilyId: null,
  combatFamilyName: null,
};

/** Действие, записываемое в журнал аудита. */
export type AuditAction =
  | 'event.create'
  | 'event.update_time'
  | 'event.close'
  | 'event.reopen'
  | 'event.cancel'
  | 'event.notify'
  | 'event.voice_check'
  | 'event.result'
  | 'event.territory'
  | 'event.delete'
  | 'member.join'
  | 'member.leave'
  | 'member.promote'
  | 'member.demote'
  | 'member.remove'
  | 'profile.upsert'
  | 'profile.edit'
  | 'profile.delete'
  | 'family.submit'
  | 'family.accept'
  | 'family.invite_call'
  | 'family.reject'
  | 'rollback.request'
  | 'rollback.submit'
  | 'rollback.channel'
  | 'rollback.channel_failed'
  | 'combat.family'
  | 'role.aptx_granted'
  | 'role.cleared'
  | 'role.tier_changed'
  | 'settings.update';

/** Откуда пришло действие. */
export type AuditSource = 'discord' | 'web' | 'system';

/** Запись журнала аудита. */
export interface AuditEntry {
  id: number;
  action: AuditAction;
  source: AuditSource;
  /** Кто выполнил действие. `null` — если действие выполнено системой. */
  actorId: string | null;
  /** Кого действие затронуло, если применимо. */
  targetId: string | null;
  /** Связанный набор, если применимо. */
  eventId: number | null;
  /** Произвольные подробности действия. */
  details: Record<string, unknown>;
  createdAt: IsoTimestamp;
}
